import { RefObject, useState } from 'react';
import { CredentialsModal } from '../CredentialsModal/CredentialsModal';
import { DeleteUserModal } from '../DeleteUserModal/DeleteUserModal';
import { DisableUserModal } from '../DisableUserModal/DisableUserModal';
import { EditUserModal } from '../EditUserModal/EditUserModal';
import styles from './UserMenu.module.scss';
import cx from 'classnames';
import { DropDownListMenu, DropDownMenuItem, DropDownMobileMenu } from 'components';
import { useDevice } from 'hooks';
import { usersActions } from 'store/users/slice';
import { useDispatch } from 'store/utils';
import { User, UserRole, UserStatus } from 'types';

type Props = {
  user: User;
  tableRef: RefObject<HTMLDivElement>;
};

export const UserMenu = ({ user, tableRef }: Props) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [credentialsModalIsOpen, setCredentialsModalIsOpen] = useState(false);
  const [isEnableLoading, setIsEnableLoading] = useState(false);

  const { isTablet } = useDevice();

  const dispatch = useDispatch();

  let contentItems = null;

  const onOptionClickHandler = (callback: () => void) => () => {
    callback();
    setOpen(false);
  };

  const enableUserHandler = async () => {
    if (isEnableLoading) return;
    try {
      setIsEnableLoading(true);
      await dispatch(
        usersActions.updateUser({
          userID: user.id,
          userData: {
            status: UserStatus.ENABLED,
          },
        }),
      );
      setOpen(false);
    } catch (err) {
      console.log(err);
    } finally {
      setIsEnableLoading(false);
    }
  };

  switch (user.status) {
    case UserStatus.ACTIVE: {
      contentItems = (
        <>
          <DropDownMenuItem
            iconName="Edit"
            name="Edit user"
            isTouchDevice={isTablet}
            onClick={onOptionClickHandler(() => {
              setEditing(true);
            })}
          />
          <DropDownMenuItem
            iconName="Disable"
            name="Disable user"
            isTouchDevice={isTablet}
            onClick={onOptionClickHandler(() => {
              setDisabling(true);
            })}
            warn
          />
          {user.role === UserRole.CLINIC && (
            <DropDownMenuItem
              name="Get credentials"
              iconName="Info"
              isTouchDevice={isTablet}
              onClick={onOptionClickHandler(() => {
                setCredentialsModalIsOpen(true);
              })}
            />
          )}
        </>
      );
      break;
    }
    case UserStatus.DISABLED: {
      contentItems = (
        <>
          <DropDownMenuItem iconName="Enable" name="Enable user" isTouchDevice={isTablet} onClick={enableUserHandler} />
        </>
      );
      break;
    }
    case UserStatus.PENDING: {
      contentItems = (
        <>
          <DropDownMenuItem
            iconName="Edit"
            name="Edit user"
            isTouchDevice={isTablet}
            onClick={onOptionClickHandler(() => {
              setEditing(true);
            })}
          />
          <DropDownMenuItem
            iconName="Update"
            name="Resend the link"
            isTouchDevice={isTablet}
            onClick={onOptionClickHandler(() => {
              dispatch(usersActions.resendInvite(user.id));
            })}
          />
          <DropDownMenuItem
            iconName="Delete"
            name="Delete"
            isTouchDevice={isTablet}
            warn
            onClick={onOptionClickHandler(() => {
              setDeleting(true);
            })}
          />
        </>
      );
      break;
    }
    default: {
      contentItems = null;
    }
  }

  return (
    <>
      <div className={styles.container}>
        <button className={cx(styles.buttonContainer, { [styles.opened]: open })} onClick={() => setOpen(true)}>
          <span className={styles.buttonCircle} />
          <span className={styles.buttonCircle} />
          <span className={styles.buttonCircle} />
        </button>
        {isTablet ? (
          <>{open && <DropDownMobileMenu onClose={() => setOpen(false)}>{contentItems}</DropDownMobileMenu>}</>
        ) : (
          <DropDownListMenu isOpen={open} onClose={() => setOpen(false)} tableRef={tableRef}>
            {contentItems}
          </DropDownListMenu>
        )}
      </div>
      {editing && <EditUserModal user={user} onClose={() => setEditing(false)} />}
      {disabling && <DisableUserModal onClose={() => setDisabling(false)} userId={user.id} />}
      {deleting && <DeleteUserModal onClose={() => setDeleting(false)} name={user.name} userId={user.id} />}
      {credentialsModalIsOpen && <CredentialsModal onClose={() => setCredentialsModalIsOpen(false)} userId={user.id} />}
    </>
  );
};
